import { DefaultError, useMutation, useQuery } from "@tanstack/react-query";
import { useQueryToast } from "hooks";
import i18next from "i18next";
import { useStoreState } from "providers/StoreStateContext";
import { apiClient } from "utils";
import { cacheService } from "utils/service/cacheService";
import { useResponseHandler } from "utils/service/responseHandler";
import { ICreateLabel, IEditLabel, ILabelResponse } from "./types";

export const useGetLabel = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  const { tokenData } = useStoreState();

  const isEnabled = Boolean(enabled && tokenData?.CurrentAccount);

  return useQuery<ILabelResponse, DefaultError>({
    queryKey: ["label", tokenData?.CurrentAccount ?? "no-account"],
    queryFn: async () => {
      const response = await apiClient({
        method: "GET",
        url: `Label`,
      });

      return responseHandler(response, {
        groupName: `useGetLabel`,
        notFoundPath: "/label",
      });
    },

    enabled: isEnabled,
    retry: false,

    refetchOnMount: false,

    ...cacheService(),
  });
};

export const usePostLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePostLabel");
  return useMutation<ILabelResponse, DefaultError, ICreateLabel>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.create.loading", {
          what: body.labelName ?? i18next.t("label.what"),
        }),
      );

      const response = await apiClient({ method: "POST", url: "Label", body });

      return responseHandler(response, {
        groupName: "usePostLabel",
        notFoundPath: "/label",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.create.success", {
          what: data.labelName ?? i18next.t("label.what"),
        }),
      );
    },
    onError: dismiss,
  });
};

export const usePutLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePutLabel");

  return useMutation<ILabelResponse, DefaultError, IEditLabel>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: body.labelName ?? i18next.t("label.what"),
        }),
      );

      const response = await apiClient({
        method: "PUT",
        url: "Label",
        body,
      });

      return responseHandler(response, {
        groupName: "usePutLabel",
        notFoundPath: "/label",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: data.labelName ?? i18next.t("label.what"),
        }),
      );
    },
    onError: dismiss,
  });
};
